/**
 * Projections toolbar for the what-if simulator: load a saved projection, save
 * the current overrides (update or save-as), rename, delete, or start fresh.
 * Projections are shared across everyone with Reporting access.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { Save, SaveAs, DriveFileRenameOutline, DeleteOutline, NoteAdd } from '@mui/icons-material';
import type { ProfitProjection } from '@techgitter/contracts';

interface Props {
  projections: ProfitProjection[];
  activeId: string | null;
  dirty: boolean;
  busy: boolean;
  onLoad: (id: string) => void;
  onNew: () => void;
  onSave: () => void;
  onSaveAs: (name: string) => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}

export const ProjectionsBar: React.FC<Props> = ({
  projections,
  activeId,
  dirty,
  busy,
  onLoad,
  onNew,
  onSave,
  onSaveAs,
  onRename,
  onDelete,
}) => {
  const active = projections.find(p => p.id === activeId) ?? null;
  const [dialog, setDialog] = useState<{ mode: 'saveAs' | 'rename'; value: string } | null>(null);

  const openSaveAs = () => setDialog({ mode: 'saveAs', value: active ? `${active.name} copy` : '' });
  const openRename = () => active && setDialog({ mode: 'rename', value: active.name });

  const submit = () => {
    if (!dialog) return;
    const name = dialog.value.trim();
    if (!name) return;
    if (dialog.mode === 'saveAs') onSaveAs(name);
    else onRename(name);
    setDialog(null);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
      <FormControl size="small" sx={{ minWidth: 240 }}>
        <InputLabel id="projection-select-label">Projection</InputLabel>
        <Select
          labelId="projection-select-label"
          label="Projection"
          value={active ? active.id : ''}
          displayEmpty
          onChange={e => e.target.value && onLoad(String(e.target.value))}
          renderValue={() => (active ? active.name : <em>Unsaved projection</em>)}
        >
          <MenuItem value="" disabled>
            <em>Unsaved projection</em>
          </MenuItem>
          {projections.map(p => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {dirty && <Chip size="small" color="warning" label="Unsaved changes" />}

      <Box sx={{ flexGrow: 1 }} />

      <Tooltip title="Start a new (empty) projection">
        <span>
          <Button size="small" startIcon={<NoteAdd />} onClick={onNew} disabled={busy}>
            New
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={active ? 'Save changes to this projection' : 'Save as a new projection'}>
        <span>
          <Button
            size="small"
            variant="contained"
            startIcon={<Save />}
            onClick={active ? onSave : openSaveAs}
            disabled={busy || (!!active && !dirty)}
          >
            Save
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Save the current overrides as a new projection">
        <span>
          <Button size="small" startIcon={<SaveAs />} onClick={openSaveAs} disabled={busy}>
            Save as
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Rename this projection">
        <span>
          <Button
            size="small"
            startIcon={<DriveFileRenameOutline />}
            onClick={openRename}
            disabled={busy || !active}
          >
            Rename
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Delete this projection">
        <span>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutline />}
            onClick={onDelete}
            disabled={busy || !active}
          >
            Delete
          </Button>
        </span>
      </Tooltip>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{dialog?.mode === 'rename' ? 'Rename projection' : 'Save projection as'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Projection name"
            value={dialog?.value ?? ''}
            onChange={e => setDialog(d => (d ? { ...d, value: e.target.value } : d))}
            onKeyDown={e => {
              if (e.key === 'Enter') submit();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={!dialog?.value.trim()}>
            {dialog?.mode === 'rename' ? 'Rename' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
